import { Fragment, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Icon } from "./Icon";
import { Card, Stack } from "./Layout";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "./Table";

// ---------------------------------------------------------------------------
// Skeleton — animated placeholder block. Pass any Tailwind class for
// size/shape via `className` (e.g. "h-4 w-32", "h-8 w-8 rounded-full").
// ---------------------------------------------------------------------------

export type SkeletonProps = HTMLAttributes<HTMLSpanElement>;

export function Skeleton({ className, ...props }: SkeletonProps) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block animate-pulse rounded bg-ink-100 align-middle dark:bg-ink-200/40",
        className,
      )}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// SkeletonLines — vertical stack of text-line skeletons.
// ---------------------------------------------------------------------------

export interface SkeletonLinesProps {
  lines: number;
  /** CSS widths cycled per line (e.g. ["80%", "60%"]). Defaults to "100%". */
  widths?: string[];
  /** Tailwind height class per line. Default: "h-4". */
  height?: string;
  className?: string;
}

export function SkeletonLines({ lines, widths, height = "h-4", className }: SkeletonLinesProps) {
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {Array.from({ length: lines }).map((_, i) => {
        const width = widths && widths.length > 0 ? widths[i % widths.length] : "100%";
        return (
          <Skeleton
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list, no reorder
            key={i}
            className={height}
            style={{ width }}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkeletonTable — table-shaped placeholder. Reuses the real Table primitives
// so column padding, border, and zebra striping match the populated state.
// ---------------------------------------------------------------------------

export interface SkeletonTableProps {
  rows: number;
  columns: number;
  className?: string;
}

export function SkeletonTable({ rows, columns, className }: SkeletonTableProps) {
  return (
    <Table className={className}>
      <TableHead>
        <TableRow>
          {Array.from({ length: columns }).map((_, c) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton grid
            <TableHeaderCell key={c}>
              <Skeleton className="h-3 w-20" />
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody className="[&>tr:nth-child(even)]:bg-ink-50/40 dark:[&>tr:nth-child(even)]:bg-ink-100/20">
        {Array.from({ length: rows }).map((_, r) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton grid
          <TableRow key={r}>
            {Array.from({ length: columns }).map((_, c) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton grid
              <TableCell key={c}>
                <Skeleton className="h-4" style={{ width: c === columns - 1 ? "60%" : "85%" }} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// SkeletonList — vertical list of rows. Mirrors the divide-y pattern used
// by member/invitation/category lists in the app.
// ---------------------------------------------------------------------------

export interface SkeletonListProps {
  rows: number;
  className?: string;
}

export function SkeletonList({ rows, className }: SkeletonListProps) {
  return (
    <ul className={cn("divide-y divide-ink-100", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list
        <li key={i} className="flex items-center justify-between gap-3 py-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-4" style={{ width: "35%" }} />
            <Skeleton className="h-3" style={{ width: "55%" }} />
          </div>
          <Skeleton className="h-4 w-16 shrink-0" />
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// SkeletonCardGrid — responsive grid of card placeholders.
// ---------------------------------------------------------------------------

export interface SkeletonCardGridProps {
  count: number;
  /** Columns at >=sm. Default 2. */
  columns?: 1 | 2 | 3;
  className?: string;
}

const gridColsClass = {
  1: "",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
} as const;

export function SkeletonCardGrid({ count, columns = 2, className }: SkeletonCardGridProps) {
  return (
    <div className={cn("grid gap-3", gridColsClass[columns], className)}>
      {Array.from({ length: count }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton grid
        <Card key={i}>
          <Stack gap={3}>
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
              <Skeleton className="h-4" style={{ width: "50%" }} />
            </div>
            <Skeleton className="h-3" style={{ width: "75%" }} />
            <Skeleton className="h-3" style={{ width: "60%" }} />
          </Stack>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkeletonBreadcrumb — mimics the Breadcrumb component's flex row: icon-sized
// chip, chevron-right separator, name placeholder. Visually stable swap for
// `<HouseholdBreadcrumb />` during page-level loading.
// ---------------------------------------------------------------------------

export interface SkeletonBreadcrumbProps {
  /** Number of segments. Default 2. */
  segments?: number;
  className?: string;
}

export function SkeletonBreadcrumb({ segments = 2, className }: SkeletonBreadcrumbProps) {
  return (
    <div className={cn("-ml-2.5 flex flex-wrap items-center gap-x-0.5 gap-y-1", className)}>
      {Array.from({ length: segments }).map((_, i) => {
        const isFirst = i === 0;
        const isLast = i === segments - 1;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton row
          <Fragment key={i}>
            {!isFirst && (
              <Icon name="chevron-right" size="sm" className="mx-0.5 shrink-0 text-ink-300" />
            )}
            <span className="inline-flex items-center gap-1.5 px-2.5 py-2">
              <Skeleton className="h-4 w-4 shrink-0 rounded" />
              <Skeleton className="h-4" style={{ width: isLast ? "7rem" : "5rem" }} />
            </span>
          </Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SkeletonGate — conditional wrapper. Renders `fallback` (or a shape preset)
// while `loading`, otherwise renders `children`. Replaces the
// `{query.isPending ? <Spinner /> : <Content />}` pattern.
// ---------------------------------------------------------------------------

export interface SkeletonGateProps {
  loading: boolean;
  children: ReactNode;
  /** Explicit skeleton tree. Wins over `shape`. */
  fallback?: ReactNode;
  /** Built-in shape preset, used when `fallback` is absent. */
  shape?: "table" | "list" | "cards" | "lines";
  /** Forwarded to table/list shapes. */
  rows?: number;
  /** Forwarded to table shape. */
  columns?: number;
  /** Forwarded to cards shape. */
  count?: number;
  /** Forwarded to lines shape. */
  lines?: number;
  /** Forwarded to cards shape. */
  cardColumns?: 1 | 2 | 3;
  /** Optional sr-only announcement (e.g. t.common.loading). */
  label?: string;
  className?: string;
}

export function SkeletonGate({
  loading,
  children,
  fallback,
  shape,
  rows = 4,
  columns = 4,
  count = 4,
  lines = 3,
  cardColumns = 2,
  label,
  className,
}: SkeletonGateProps) {
  if (!loading) return <>{children}</>;

  let body: ReactNode;
  if (fallback !== undefined) {
    body = fallback;
  } else {
    switch (shape) {
      case "table":
        body = <SkeletonTable rows={rows} columns={columns} />;
        break;
      case "cards":
        body = <SkeletonCardGrid count={count} columns={cardColumns} />;
        break;
      case "lines":
        body = <SkeletonLines lines={lines} />;
        break;
      default:
        body = <SkeletonList rows={rows} />;
    }
  }

  return (
    <output aria-busy="true" className={cn("block", className)}>
      {label && <span className="sr-only">{label}</span>}
      {body}
    </output>
  );
}
